import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import { TPaymentAccordionInstance } from 'widgets/checkout/PaymentAccordion';
import { TCheckoutcomComponentInstance } from 'core/checkout/CheckoutcomComponent';
import { TCheckoutcomWalletComponentInstance, isCheckoutcomWalletMethod } from 'core/checkout/CheckoutcomWalletComponent';
import { TSummaryFormInstance } from 'core/checkout/CoreSummaryForm';
import { TPaymentResult } from 'widgets/checkout/CheckoutMgr';

const CHECKOUT_COM_SUBMIT_ACTION = 'checkoutcom_submit';
const CHECKOUT_COM_CARD_METHOD_ID = 'checkout_com_card';
const CHECKOUT_COM_WALLET_CONTAINER_SELECTOR = '[data-checkoutcom-wallet-button]';

type TOrderBilling = {
    billing?: {
        payment?: {
            selectedPaymentInstruments?: Array<{
                paymentMethod?: string;
            }>;
        };
    };
};

/**
 * @description Intercepts the Place Order response for Checkout.com card
 * payments. The server-side flow creates the SFCC order during PlaceOrder
 * and returns action='checkoutcom_submit' so this mixin can hand off to
 * the already mounted Flow SDK component. The SDK's onPaymentCompleted
 * callback then redirects to Checkoutcom-HandleRedirect, which finalises
 * the order using the pspReference stamped by the auth hook.
 * @param SummaryStepMixin Base widget for extending
 * @returns CheckoutcomSummaryMixin class
 */
function CheckoutcomSummaryMixinClassCreator(SummaryStepMixin: import('core/checkout/CoreSummaryStepMixin').TCoreSummaryStepMixin) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class CheckoutcomSummaryMixin
     * @augments SummaryStepMixin
     * @classdesc Routes Checkout.com card payments through the standard
     * PlaceOrder server call (which now creates the order before redirect)
     * and triggers the Flow SDK only once the order has been authorized.
     *
     * Checkout.com wallets (Apple Pay) cannot be submitted programmatically, so
     * for them the summary step renders the wallet button in place of Place
     * Order, and the order is placed from within the wallet's own submission.
     */
    class CheckoutcomSummaryMixin extends SummaryStepMixin {
        prefs() {
            return {
                errorCheckoutcomSubmit: '',
                ...super.prefs()
            };
        }

        /**
         * @description Handle PlaceOrder response. When the server returns
         * action='checkoutcom_submit' the order has been created and the
         * client must invoke the mounted Flow SDK to actually take the
         * payment. All other actions fall through to the standard handler.
         * @param response - Server response object
         * @param submitBtn - Submit button used to manage processing state
         */
        handleSummarySubmissionResponse(response: TPaymentResult, submitBtn: TProcessButtonInstance) {
            if (response && response.success && response.action === CHECKOUT_COM_SUBMIT_ACTION) {
                this.invokeCheckoutcomSdk(submitBtn);

                return;
            }

            super.handleSummarySubmissionResponse(response, submitBtn);
        }

        /**
         * @description Adds the flag that swaps Place Order for the Checkout.com wallet button
         * @returns Next step button data
         */
        getNextButtonData() {
            return {
                ...super.getNextButtonData(),
                isCheckoutcomWalletSummary: isCheckoutcomWalletMethod(this.getSelectedPaymentMethodId())
            };
        }

        /**
         * @description Mounts the Checkout.com wallet button once the summary controls were rendered.
         * Runs on every render of the controls, the wallet item ignores mounts into the container it
         * already occupies.
         * @listens Label#updated
         */
        mountCheckoutcomWalletButton() {
            const methodId = this.getSelectedPaymentMethodId();
            const container = this.ref('self').get()?.querySelector(CHECKOUT_COM_WALLET_CONTAINER_SELECTOR);

            if (!container || !isCheckoutcomWalletMethod(methodId)) {
                return;
            }

            this.getByIdDeep('payment', (paymentAccordion: TPaymentAccordionInstance) => {
                const walletItem = paymentAccordion.getMethod(String(methodId)) as unknown as TCheckoutcomWalletComponentInstance | null;

                if (!walletItem || typeof walletItem.mountWalletButton !== 'function') {
                    this.showCheckoutcomSubmitError();

                    return;
                }

                walletItem.mountWalletButton(container as HTMLElement, {
                    validate: () => this.validateCheckoutcomWalletOrder(),
                    placeOrder: () => this.placeCheckoutcomWalletOrder(),
                    onError: (message: string) => this.showCheckoutcomSubmitError(undefined, message)
                });
            });
        }

        /**
         * @description Validates the summary step when the wallet button is clicked. Runs before the wallet
         * sheet opens, which has to happen within the click, so it cannot involve a server call.
         * @returns True when the order may be placed
         */
        validateCheckoutcomWalletOrder(): boolean {
            if (this.prefs().processingRequest) {
                return false;
            }

            this.ref('summaryError').hide();

            return (
                this.getById(this.prefs().summaryFormID, (summaryForm: TSummaryFormInstance) => {
                    return summaryForm.validate();
                }) || false
            );
        }

        /**
         * @description Places the order for a wallet payment the shopper has just authorized. Any failure is
         * reported here through the regular Place Order error handling, the caller only learns whether to
         * go on and submit the payment.
         * @returns Promise resolved with true once the order was created for the Checkout.com payment
         */
        placeCheckoutcomWalletOrder(): Promise<boolean> {
            if (this.prefs().processingRequest) {
                return Promise.resolve(false);
            }

            this.ref('summaryError').hide();
            this.setPref('processingRequest', true);
            this.eventBus().emit('checkout.summary.step.submitted', this);

            const submission = this.submitSummaryForm();

            if (!submission) {
                this.setPref('processingRequest', false);

                return Promise.resolve(false);
            }

            return submission
                .then(response => {
                    const paymentResult = response as unknown as TPaymentResult;

                    if (paymentResult && paymentResult.success && paymentResult.action === CHECKOUT_COM_SUBMIT_ACTION) {
                        return true;
                    }

                    // no Place Order button to hand back on this path
                    this.handleSummarySubmissionResponse(paymentResult, undefined as unknown as TProcessButtonInstance);

                    return false;
                })
                .catch(() => {
                    this.setPref('processingRequest', false);

                    return false;
                });
        }

        /**
         * @description Returns selected payment method ID from order model
         * @returns Payment method ID or undefined
         */
        getSelectedPaymentMethodId(): string | undefined {
            const order = this.order as unknown as TOrderBilling | undefined;

            return order?.billing?.payment?.selectedPaymentInstruments?.[0]?.paymentMethod;
        }

        /**
         * @description Submit the mounted Checkout.com Flow component.
         * The SDK's success_url already points back to Checkoutcom-HandleRedirect.
         * @param submitBtn - Submit button used to manage processing state
         */
        invokeCheckoutcomSdk(submitBtn: TProcessButtonInstance) {
            const methodId = this.getSelectedPaymentMethodId() || CHECKOUT_COM_CARD_METHOD_ID;

            this.getByIdDeep('payment', (paymentAccordion: TPaymentAccordionInstance) => {
                const checkoutcomItem = paymentAccordion.getMethod(methodId) as unknown as TCheckoutcomComponentInstance | null;

                if (!checkoutcomItem || typeof checkoutcomItem.submitPayment !== 'function') {
                    this.showCheckoutcomSubmitError(submitBtn);

                    return;
                }

                checkoutcomItem.submitPayment().catch((error: Error) => {
                    this.showCheckoutcomSubmitError(submitBtn, error?.message);
                });
            });
        }

        /**
         * @description Surface a recoverable error after the order was created server-side, and
         * hand the Place Order button back to the shopper. The Checkout.com accordion item reports
         * its own errors inline, but it is collapsed on this step, so anything that goes wrong
         * between Place Order and the payment provider has to be rendered here to be seen at all.
         * @param submitBtn - Submit button used to restore default state
         * @param message - Message reported by the payment component, when it had one
         */
        showCheckoutcomSubmitError(submitBtn?: TProcessButtonInstance, message?: string) {
            const summary = this.ref('summaryError');

            summary.setText(message || this.prefs().errorCheckoutcomSubmit).show();

            const summaryElement = summary.get();

            if (summaryElement) {
                scrollWindowTo(summaryElement, true);
            }

            this.setPref('processingRequest', false);
            submitBtn?.stopProcess();
        }
    }

    return CheckoutcomSummaryMixin;
}

export type TCheckoutcomSummaryMixin = ReturnType<typeof CheckoutcomSummaryMixinClassCreator>;
export type TCheckoutcomSummaryMixinInstance = InstanceType<TCheckoutcomSummaryMixin>;

export default CheckoutcomSummaryMixinClassCreator;
